import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Server } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DatasourceActionCell } from "@/components/documents/tables/datasource-actions";
import { IDCell } from "@/components/shared/id-cell";
import { SortableColumn } from "@/components/shared/sortable-column";
import { format } from "date-fns";


export function getDatasourceColumns({
    setActiveSection,
    setSelectedDataSource,
    sortBy,
    sortOrder,
    handleSort,
}: {
    setActiveSection: (section: string) => void;
    setSelectedDataSource: (ds: DataSource) => void;
    sortBy: string;
    sortOrder: string;
    handleSort: (column: string) => void;
}): ColumnDef<DataSource>[] {
    return [
        {
            accessorKey: "name",
            header: () => (
                <SortableColumn label="Name" columnKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
            ),
            cell: ({ row }) => {
                const description = row.original.description as string | undefined;
                return (
                    <div
                        className="flex items-start gap-3 cursor-pointer pl-4 group"
                        onClick={() => {
                            setSelectedDataSource(row.original);
                            setActiveSection("details");
                        }}
                    >
                        <Server className="h-4 w-4 mt-0.5 shrink-0 group-hover:text-primary transition-colors" />
                        <div className="min-w-0">
                            <p className="font-medium group-hover:text-primary transition-colors truncate">
                                {row.getValue("name")}
                            </p>
                            {description?.trim() ? (
                                <p className="text-xs text-muted-foreground truncate max-w-[280px]">
                                    {description}
                                </p>
                            ) : null}
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: "dataset_id",
            header: () => <div className="inline-flex items-center h-9 px-4">Datasource ID</div>,
            cell: ({ row }) => {
                const datasetId = row.original.dataset_id;
                return (
                    <div className="pl-4">
                        {datasetId ? (
                            <IDCell id={datasetId} />
                        ) : (
                            <span className="text-muted-foreground">N/A</span>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: "document_count",
            header: () => <div className="inline-flex items-center h-9 px-4">Documents</div>,
            cell: ({ row }) => (
                <div className="pl-4">
                    <Badge variant="secondary">
                        {row.getValue("document_count")} {row.getValue("document_count") === 1 ? "document" : "documents"}
                    </Badge>
                </div>
            ),
        },
        {
            accessorKey: "updated_at",
            header: () => (
                <SortableColumn label="Updated At" columnKey="updated_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
            ),
            cell: ({ row }) => {
                const val = row.getValue("updated_at") as string | undefined;
                if (!val) return <span className="pl-4 text-muted-foreground">—</span>;
                const date = new Date(val);
                return (
                    <div className="flex flex-col pl-4">
                        <span>{format(date, "MMM d, yyyy")}</span>
                        <span className="text-muted-foreground text-sm">{format(date, "hh:mm a")}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "created_at",
            header: () => (
                <SortableColumn label="Created At" columnKey="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
            ),
            cell: ({ row }) => {
                const date = new Date(row.getValue("created_at"));
                return (
                    <div className="flex flex-col pl-4">
                        <span>{format(date, "MMM d, yyyy")}</span>
                        <span className="text-muted-foreground text-sm">{format(date, "hh:mm a")}</span>
                    </div>
                );
            },
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => <DatasourceActionCell row={row} />,
        },
    ];
}
